import { createElement } from 'react';
import { type FontStyle, type FontWeight } from 'satori';

import { resolveExportDimensionsForFormat } from '../../nodes/Image/smartResize/utils/outputDimensions';
import { DEFAULT_FONT_SIZE, DEFAULT_LETTER_SPACING, DEFAULT_LINE_HEIGHT } from '../../nodes/Text/constants';
import { DEFAULT_FONT_WEIGHT, getFontFamily, getLoadedFonts, parseFontVariant } from '../../nodes/Text/fonts';
import { type AppNode } from '../../types';
import { canvasToBlob, loadImage } from '../canvasHelpers';
import { drawImageObjectCover } from '../drawImageObjectCover';
import { getNodeDimensions } from '../nodeDimensions';
import { type TextNode } from '../typeGuards';
import { loadSatoriEmojiAsset } from './loadSatoriEmojiAsset';
import { computeGroupBounds } from './nodeGrouping';

let satoriModulePromise: Promise<typeof import('satori')> | undefined;

const loadSatori = () =>
  (satoriModulePromise ??= import('satori').catch((error: unknown) => {
    satoriModulePromise = undefined;
    throw error;
  }));

const getImageUrl = (node: AppNode): string | undefined => {
  if (node.type === 'image') {
    return node.data.media?.image ?? node.data.media?.original;
  }

  if (node.type === 'video') {
    return node.data.thumbnailUrl ?? node.data.media?.image;
  }

  return undefined;
};

const toFontWeight = (value: number): FontWeight => {
  // biome-ignore lint/style/noMagicNumbers: standard CSS font-weight values
  const valid: FontWeight[] = [100, 200, 300, 400, 500, 600, 700, 800, 900];

  return valid.find((w) => w === value) ?? DEFAULT_FONT_WEIGHT;
};

const fetchFontsForSatori = async (neededFamilies: Set<string>) => {
  // Only fetch fonts whose family is actually referenced by a text node in the
  // group — the registry holds every font loaded session-wide, and signed font
  // URLs can expire. Filtering avoids unrelated failures aborting the export.
  const entries = getLoadedFonts().filter((e) => neededFamilies.has(e.family));

  // Promise.allSettled so a single failed font fetch (expired URL, network blip)
  // degrades to a system-font fallback in satori rather than killing the composite.
  const results = await Promise.allSettled(
    entries.map(async (entry) => {
      const res = await fetch(entry.url);

      if (!res.ok) {
        throw new Error(`Font fetch failed: ${res.status}`);
      }

      const style: FontStyle = entry.style === 'italic' ? 'italic' : 'normal';
      const weight: FontWeight = toFontWeight(Number(entry.weight));

      return {
        data: await res.arrayBuffer(),
        name: entry.family,
        style,
        weight,
      };
    }),
  );

  return results.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : []));
};

const collectNeededFontFamilies = (members: AppNode[]): Set<string> => {
  const families = new Set<string>();

  for (const node of members) {
    if (node.type === 'text' && node.data.text?.trim()) {
      families.add(getFontFamily(node.data.fontFamily ?? 'Inter'));
    }
  }

  return families;
};

type SatoriFont = Awaited<ReturnType<typeof fetchFontsForSatori>>[number];

const renderTextNodeToCanvas = async (
  node: TextNode,
  nodeWidth: number,
  nodeHeight: number,
  fonts: SatoriFont[],
): Promise<HTMLCanvasElement> => {
  const { data } = node;
  const fontSize = data.fontSize ?? DEFAULT_FONT_SIZE;
  const fontFamily = getFontFamily(data.fontFamily ?? 'Inter');
  const fontColor = data.fontColor ?? '#000000';
  const textAlign = data.textAlign ?? 'left';
  const lineHeight = data.lineHeight ?? DEFAULT_LINE_HEIGHT;
  const letterSpacing = data.letterSpacing ?? DEFAULT_LETTER_SPACING;
  const { fontWeight, fontStyle } = parseFontVariant(data.fontVariant);
  const text = data.text ?? '';

  // Render wider than the node so glyph edges (overhangs/italics) aren't clipped
  // by satori's tight layout box. The caller offsets the draw position to compensate.
  // biome-ignore lint/style/noMagicNumbers: fraction of fontSize for glyph overhang buffer
  const pad = Math.ceil(fontSize / 4);
  const renderWidth = nodeWidth + pad;

  const element = createElement(
    'div',
    {
      style: {
        boxSizing: 'border-box',
        color: fontColor,
        display: 'flex',
        flexDirection: 'column',
        fontFamily,
        fontSize,
        fontStyle,
        fontWeight,
        height: '100%',
        letterSpacing,
        lineHeight,
        paddingLeft: pad,
        textAlign,
        whiteSpace: 'pre-wrap',
        width: '100%',
        wordBreak: 'break-word',
      },
    },
    text,
  );

  const { default: satori } = await loadSatori();

  const svg = await satori(element, {
    fonts,
    height: nodeHeight,
    loadAdditionalAsset: (languageCode, segment) => loadSatoriEmojiAsset(languageCode, segment, fontSize),
    width: renderWidth,
  });

  // Object URL avoids nesting emoji PNG data-URLs inside an SVG data-URL, which
  // some browsers fail to decode past a size threshold. Omit charset — Safari
  // rejects `image/svg+xml;charset=utf-8` blobs.
  const svgUrl = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));

  try {
    const img = await loadImage(svgUrl);

    const canvas = document.createElement('canvas');
    canvas.width = renderWidth;
    canvas.height = nodeHeight;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Could not get 2d canvas context');
    }

    ctx.drawImage(img, 0, 0, renderWidth, nodeHeight);

    return canvas;
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
};

export type CompositeGroupOptions = {
  /** When set, center-crops the composite to the format's canonical export size (matches platform preview). */
  exportFormatId?: string;
};

export const compositeGroup = async (members: AppNode[], options?: CompositeGroupOptions): Promise<Blob> => {
  const bounds = computeGroupBounds(members);

  if (!bounds) {
    throw new Error('Cannot composite an empty group');
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(bounds.width);
  canvas.height = Math.ceil(bounds.height);

  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not get 2d canvas context');
  }

  const sortedMembers = [...members].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
  const neededFamilies = collectNeededFontFamilies(sortedMembers);
  const fonts = neededFamilies.size > 0 ? await fetchFontsForSatori(neededFamilies) : [];

  for (const node of sortedMembers) {
    const { width, height } = getNodeDimensions(node);
    const x = node.position.x - bounds.minX;
    const y = node.position.y - bounds.minY;

    if (width <= 0 || height <= 0) {
      continue;
    }

    if (node.type === 'text') {
      const text = node.data.text ?? '';

      if (text.trim()) {
        try {
          const renderedCanvas = await renderTextNodeToCanvas(node, width, height, fonts);
          // The rendered canvas is wider than the node by `pad` to avoid glyph clipping;
          // shift left so text aligns at (x, y).
          const pad = renderedCanvas.width - width;
          ctx.drawImage(renderedCanvas, x - pad, y);
        } catch {
          // Don't propagate the underlying error — it can include the user's
          // text (satori output, image URLs) and is forwarded to Bugsnag.
          throw new Error('Failed to render text node');
        }
      }

      continue;
    }

    const imageUrl = getImageUrl(node);

    if (!imageUrl) {
      continue;
    }

    try {
      const img = await loadImage(imageUrl);
      drawImageObjectCover(ctx, img, x, y, width, height);
    } catch {
      // A single broken asset shouldn't fail the whole composite; the group still
      // composites with the remaining nodes and the user gets a partial result.
    }
  }

  const exportDimensions = options?.exportFormatId
    ? resolveExportDimensionsForFormat(options.exportFormatId)
    : undefined;

  if (exportDimensions) {
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = Math.ceil(exportDimensions.width);
    exportCanvas.height = Math.ceil(exportDimensions.height);

    const exportCtx = exportCanvas.getContext('2d');

    if (!exportCtx) {
      throw new Error('Could not get 2d canvas context');
    }

    drawImageObjectCover(exportCtx, canvas, 0, 0, exportDimensions.width, exportDimensions.height);

    return canvasToBlob(exportCanvas, 'image/png');
  }

  return canvasToBlob(canvas, 'image/png');
};
